import { PagenatedResponse } from '@models/common';
import { ProductModel } from '@models/product';
import {
  ChatCounts,
  Counts,
  CumulativeHourlySales,
  DailyCount,
  DailyNetSales,
  DailyPaymentCount,
  DailyPaymentNewCount,
  DailyPointUsageCount,
  MonthlyPaymentComparisonData,
  PartnerProfileClickCountData,
  PartnerProfileViewCountData,
  PaymentCounts,
  PaymentRankData,
  ProductClickLocationData,
  DiscoverFeedStats,
  ProductCount,
  ProductCounts,
  ProductCountSummary,
  ProductExposeCount,
  PurchasesReport,
  SalesReport,
  UserCounts,
} from '@models/statistics';
import { apiClient } from '.';

export const getPurchasesReport = async (query: {
  startDate: Date;
  endDate: Date;
  sort: string;
  page: number;
  size: number;
}) => {
  return apiClient.get<PagenatedResponse<PurchasesReport>>('/admin/v1/statistics/user-purchases-report', {
    params: query,
  });
};

export const getPurchasesReportExcelFile = async (query: {
  startDate: Date;
  endDate: Date;
  sort: string;
  page: number;
  size: number;
  detail?: boolean;
}) => {
  return apiClient.get(`/admin/v1/statistics/user-purchases-report/export`, {
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
    params: query,
  });
};

export const getSalesReportByPartner = async (startDate: Date, endDate: Date) => {
  return apiClient.get<SalesReport[]>('/admin/v1/statistics/sales', {
    params: { startDate, endDate },
  });
};

export const getCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<Counts>('/admin/v1/statistics/counts', {
    params: { startDate, endDate },
  });
};

export const getUsersCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<UserCounts>('/admin/v1/statistics/counts/users', {
    params: { startDate, endDate },
  });
};

export const getDailyActiveUserCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<Counts>('/admin/v1/statistics/counts/dau', {
    params: { startDate, endDate },
  });
};

export const getMonthlyActiveUserCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<Counts>('/admin/v1/statistics/counts/mau', {
    params: { startDate, endDate },
  });
};

export const getVisitCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<Counts>('/admin/v1/statistics/counts/visit', {
    params: { startDate, endDate },
  });
};

export const getNewParentsCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<Counts>('/admin/v1/statistics/counts/new-parents', {
    params: { startDate, endDate },
  });
};

export const getNewTutorsCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<Counts>('/admin/v1/statistics/counts/new-tutors', {
    params: { startDate, endDate },
  });
};

export const getNewSellersCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<Counts>('/admin/v1/statistics/counts/new-sellers', {
    params: { startDate, endDate },
  });
};

export const getNewInstructorsCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<Counts>('/admin/v1/statistics/counts/new-instructors', {
    params: { startDate, endDate },
  });
};

export const getSearchTextRank = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<
    {
      _id: string;
      count: number;
    }[]
  >('/admin/v1/statistics/keyword-rank', {
    params: { startDate, endDate },
  });
};

export const exportSearchTextRank = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get('/admin/v1/statistics/keyword-rank/export', {
    params: { startDate, endDate },
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getDateSearchRank = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{
    totalCount: number;
    dateSearchCount: number;
    rank: {
      _id: string;
      count: number;
      query?: any;
    }[];
  }>('/admin/v1/statistics/keyword-rank/date', {
    params: { startDate, endDate },
  });
};

export const getNoResultSearchTextRank = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<
    {
      _id: string;
      count: number;
    }[]
  >('/admin/v1/statistics/keyword-rank/no-result', {
    params: { startDate, endDate },
  });
};

export const exportNoResultSearchTextRank = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get('/admin/v1/statistics/keyword-rank/no-result/export', {
    params: { startDate, endDate },
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const exportDateSearchRank = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get('/admin/v1/statistics/keyword-rank/date/export', {
    params: { startDate, endDate },
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getDailyActiveUsersCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyCount[]>('/admin/v1/statistics/dau', {
    params: { startDate, endDate },
  });
};

export const getDailyJoinUsersCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyCount[]>('/admin/v1/statistics/daily-join', {
    params: { startDate, endDate },
  });
};

export const getDailyActiveSessionsCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyCount[]>('/admin/v1/statistics/daily-session', {
    params: { startDate, endDate },
  });
};

export const getDailyViewCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyCount[]>('/admin/v1/statistics/daily-view', {
    params: { startDate, endDate },
  });
};

export const getProductDailyViewCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<ProductCountSummary[]>('/admin/v1/statistics/product-report/daily-view', {
    params: { startDate, endDate },
  });
};
export const getDailyRefundCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyPaymentCount[]>('/admin/v1/statistics/daily-refund', {
    params: { startDate, endDate },
  });
};

export const getDailyPaymentCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyPaymentNewCount[]>('/admin/v1/statistics/daily-payment', {
    params: { startDate, endDate },
  });
};

export const getDailyPointUsageCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyPointUsageCount[]>('/admin/v1/statistics/daily-point-usage', {
    params: { startDate, endDate },
  });
};

export const getDailyNetSales = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyNetSales[]>('/admin/v1/statistics/daily-net-sales', {
    params: { startDate, endDate },
  });
};

export const getDailyPaymentErrorCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyPaymentCount[]>('/admin/v1/statistics/daily-payment-error', {
    params: { startDate, endDate },
  });
};

export const getDailyPaymentReadyCount = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<DailyPaymentCount[]>('/admin/v1/statistics/daily-payment-ready', {
    params: { startDate, endDate },
  });
};

export const getProductCounts = async () => {
  return apiClient.get<ProductCounts>('/admin/v1/statistics/product-counts');
};

export const getChatCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<ChatCounts>('/admin/v1/statistics/chat-counts', { params: { startDate, endDate } });
};

export const getPaymentCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<PaymentCounts>('/admin/v1/statistics/payment-counts', { params: { startDate, endDate } });
};

export const getPaymentRank = async (options: {
  startDate: string | null;
  endDate: string | null;
  saleType?: 'commerce' | 'class';
  channel?: 'naver-store' | 'kakao-reservation' | 'ezwel';
  sort: 'desc' | 'asc';
  limit: number;
}) => {
  return apiClient.get<PaymentRankData[]>('/admin/v1/statistics/payment-rank', { params: options });
};

export const getRefundRankSeller = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<PaymentRankData[]>('/admin/v1/statistics/refund-seller-rank', {
    params: { startDate, endDate },
  });
};

export const getRefundRankProduct = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<PaymentRankData[]>('/admin/v1/statistics/refund-product-rank', {
    params: { startDate, endDate },
  });
};

export const getUserCountByState = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ state: string; count: number }[]>('/admin/v1/statistics/user-region-count', {
    params: { startDate, endDate },
  });
};

export const getPageViewCount = async (options: { path: string; by: string }) => {
  return apiClient.get<PaymentRankData[]>('/admin/v1/statistics/page/view-count', { params: options });
};

export const getProductReport = async (options: {
  year: number;
  month?: number;
  day?: number;
  week?: number;
  sort?: string;
}) => {
  return apiClient.get<PagenatedResponse<ProductCount>>('/admin/v1/statistics/product-report', {
    params: options,
  });
};

export const getProductReportExcelFile = async (options: {
  year: number;
  month?: number;
  day?: number;
  week?: number;
  sort?: string;
}) => {
  return apiClient.get('/admin/v1/statistics/product-report/export', {
    params: options,
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getProductExposureReports = async (options: {
  year: number;
  month?: number;
  day?: number;
  week?: number;
  sort?: string;
}) => {
  return apiClient.get<PagenatedResponse<ProductExposeCount>>('/admin/v1/statistics/product-exposure-report', {
    params: options,
  });
};

export const getProductExposureReportExcelFile = async (options: {
  year: number;
  month?: number;
  day?: number;
  week?: number;
  sort?: string;
}) => {
  return apiClient.get('/admin/v1/statistics/product-exposure-report/export', {
    params: options,
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getConversionTotalAverage = async (options?: {
  year?: number;
  month?: number;
  day?: number;
  week?: number;
}) => {
  return apiClient.get<{
    totalProducts: number;
    totalView: number;
    totalPayment: number;
    overallConversion: number;
  }>('/admin/v1/statistics/product-report/conversion-average', {
    params: options,
  });
};

export const generateProductReport = async (options: { year: number; month?: number; day?: number }) => {
  return apiClient.post<ProductCount[]>(
    '/admin/v1/statistics/product-report',
    {},
    {
      params: options,
    },
  );
};

export const generateProductExposureReport = async (options: { year: number; month?: number; day?: number }) => {
  return apiClient.post(
    '/admin/v1/statistics/product-exposure-report',
    {},
    {
      params: options,
    },
  );
};

export const getClicksRank = async (options: { startDate: string | null; endDate: string | null }) => {
  return apiClient.get<
    {
      _id: {
        actionId: string;
        actionType: string;
        collectionId?: string;
        productId?: string;
        link?: string;
        keyword?: string;
      };
      count: number;
    }[]
  >('/admin/v1/statistics/clicks', { params: options });
};

export const exportClicksRank = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get('/admin/v1/statistics/clicks/export', {
    params: { startDate, endDate },
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getPurchaseSourceCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<
    {
      eventId: {
        actionId: string;
        keyword?: string;
        collectionId?: string;
      };
      count: number;
      collectionTitle?: string;
      partnerName?: string;
    }[]
  >('/admin/v1/statistics/purchase-source-counts', {
    params: { startDate, endDate },
  });
};

export const getCumulativeHourlySales = async (baseDate?: string) => {
  const params = baseDate ? { baseDate } : {};
  return apiClient.get<CumulativeHourlySales>('/admin/v1/statistics/sales/cumulative-hourly', { params });
};

export const exportPurchaseSourceCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get('/admin/v1/statistics/purchase-source-counts/export', {
    params: { startDate, endDate },
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getDailySessionDurationMedians = async (startDate?: string, endDate?: string) => {
  return apiClient.get<{ date: string; medianDuration: number; trimmedMeanDuration: number; sessionCount: number }[]>(
    '/admin/v1/statistics/session-duration/daily-medians',
    {
      params: { startDate, endDate },
    },
  );
};

export const getDailyProductInteractionRatios = async (startDate?: string, endDate?: string) => {
  return apiClient.get<
    {
      date: string;
      viewCount: number;
      favoriteCount: number;
      purchaseClickCount: number;
      reviewClickCount: number;
      shareCount: number;
      paymentClickCount: number;
      actualPaymentCount: number;
      favoriteRatio: number;
      purchaseRatio: number;
      reviewRatio: number;
      shareRatio: number;
      paymentRatio: number;
      actualPaymentRatio: number;
    }[]
  >('/admin/v1/statistics/product-interactions/daily-ratios', {
    params: { startDate, endDate },
  });
};

export const getPaymentTimeSlotTrends = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<
    {
      dayOfWeek: number; // 1=일요일, 2=월요일, ..., 7=토요일
      dayOfWeekString: string; // "월요일"
      timeSlots: {
        timeSlot: number; // 0-47
        timeSlotString: string; // "09:00", "09:30"
        totalCount: number;
        app: number;
        naverStore: number;
        kakaoReservation: number;
        ezwel?: number;
        appAmount: number;
        naverStoreAmount: number;
        kakaoReservationAmount: number;
        ezwelAmount?: number;
      }[];
    }[]
  >('/admin/v1/statistics/payment-timeslot-trends', {
    params: { startDate, endDate },
  });
};

export const getProductClickCountsByLocation = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<ProductClickLocationData[]>('/admin/v1/statistics/product-click-counts-by-location', {
    params: { startDate, endDate },
  });
};

export const getDiscoverFeedStats = async (startDate: string | Date | null, endDate: string | Date | null) => {
  return apiClient.get<DiscoverFeedStats>('/admin/v1/statistics/discover-feed', {
    params: { startDate, endDate },
  });
};

export const getPartnerProfileViewCounts = async (
  startDate: string | null,
  endDate: string | null,
  limit: number = 30,
) => {
  return apiClient.get<PartnerProfileViewCountData[]>('/admin/v1/statistics/partner-profile-view-counts', {
    params: { startDate, endDate, limit },
  });
};

export const getPartnerProfileClickCounts = async (
  startDate: string | null,
  endDate: string | null,
  limit: number = 30,
) => {
  return apiClient.get<PartnerProfileClickCountData[]>('/admin/v1/statistics/partner-profile-click-counts', {
    params: { startDate, endDate, limit },
  });
};

export const getMonthlyPaymentComparison = async () => {
  return apiClient.get<MonthlyPaymentComparisonData[]>('/admin/v1/statistics/monthly-payment-comparison');
};

export const getDailyPurchaseSourceCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ date: string; actionId: string; count: number }[]>(
    '/admin/v1/statistics/daily-purchase-source-counts',
    {
      params: { startDate, endDate },
    },
  );
};

export const getBuyerRegionCounts = async () => {
  return apiClient.get<{ _id: string; count: number }[]>('/admin/v1/statistics/buyer-region-counts');
};

export const getBuyerRegionPaymentAmounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ _id: string; totalAmount: number; count: number }[]>(
    '/admin/v1/statistics/buyer-region-payment-amounts',
    {
      params: { startDate, endDate },
    },
  );
};

export const getProductViewsByCategoryWeekly = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ [key: string]: any }[]>('/admin/v1/statistics/product-views-by-category-weekly', {
    params: { startDate, endDate },
    timeout: 300000,
  });
};

export const getProductViewsByCategoryWeeklyRatios = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ [key: string]: any }[]>('/admin/v1/statistics/product-views-by-category-weekly-ratios', {
    params: { startDate, endDate },
    timeout: 300000,
  });
};

export const getPartnersCounts = async () => {
  return apiClient.get<{
    totalPartners: number;
    partnersWithOpenProducts: number;
    partnersWithOpenCommerceProducts: number;
    partnersWithOpenClassProducts: number;
    partnersWithProductsButNotOpen: number;
    partnersWithCommerceProductsButNotOpen: number;
    partnersWithClassProductsButNotOpen: number;
    partnersWithoutProducts: number;
    classPartnersWithoutProducts: number;
    commercePartnersWithoutProducts: number;
    notSellingSellerIdsMissingFromUsers: number;
    warningProducts: ProductModel[];
    excellentPartnerCount: number;
    excellentPartnerEvaluatedAt: string | null;
  }>('/admin/v1/statistics/counts/partners');
};

export const getDailyClassCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ date: string; count: number; isPrediction?: boolean }[]>(
    '/admin/v1/statistics/daily-class-counts',
    {
      params: { startDate, endDate },
    },
  );
};

export const getDailyFavoriteProductCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ date: string; dailyCount: number; cumulativeCount: number }[]>(
    '/admin/v1/statistics/daily-favorite-product-counts',
    {
      params: { startDate, endDate },
    },
  );
};

export const getDailyFavoriteUserContentsCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ date: string; dailyCount: number; cumulativeCount: number }[]>(
    '/admin/v1/statistics/daily-favorite-user-contents-counts',
    {
      params: { startDate, endDate },
    },
  );
};

export const getDailyFavoritePartnerCounts = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ date: string; dailyCount: number; cumulativeCount: number }[]>(
    '/admin/v1/statistics/daily-favorite-partner-counts',
    {
      params: { startDate, endDate },
    },
  );
};

export const getProductCountsByLocation = async () => {
  return apiClient.get<{ location: string; count: number }[]>('/admin/v1/statistics/product-counts-by-location');
};

export const getProductSalesByLocation = async (startDate: string | null, endDate: string | null) => {
  return apiClient.get<{ location: string; monthlyData: { date: string; count: number; amount: number }[] }[]>(
    '/admin/v1/statistics/product-sales-by-location',
    {
      params: { startDate, endDate },
    },
  );
};

export const getTotalUnusedCreditPoints = async () => {
  return apiClient.get<{ totalCreditPoints: number }>('/admin/v1/statistics/counts/total-unused-credit-points');
};

export const getCreditPointsStatistics = async () => {
  return apiClient.get<{
    topUsers: Array<{
      _id: string;
      name: string;
      phone: string;
      email: string;
      creditPoints: number;
    }>;
    pointRangeStats: {
      above50000: number;
      above30000: number;
      above10000: number;
      aboveZero: number;
    };
  }>('/admin/v1/statistics/credit-points-statistics');
};

export const getDailyAssessmentCounts = async (startDate: string, endDate: string) => {
  return apiClient.get<{
    dailyData: Array<{
      _id: string;
      assessmentCount: number;
      userCount: number;
    }>;
    totalStats: {
      totalAssessmentCount: number;
      totalUserCount: number;
    };
  }>('/admin/v1/statistics/daily-assessment-counts', {
    params: { startDate, endDate },
  });
};

export const getNetSalesReport = async (startDate: string, endDate: string) => {
  return apiClient.get<
    Array<{
      channel: string;
      totalPaymentAmount: number;
      totalBillingAmount: number;
      totalPointAmount: number;
      totalCouponDiscountAmount: number;
      totalMembershipDiscountAmount: number;
      totalEstimatedSettlementAmount: number;
      totalTransferAmount: number;
      totalRefundAmount: number;
      totalRefundBillingAmount: number;
      estimatedTotalRefundBillingAmount: number;
      pgFee: number;
      estimatedPgFee: number;
    }>
  >('/admin/v1/statistics/net-sales-report', {
    params: { startDate, endDate },
  });
};

export const getDailyNetSalesByChannel = async (startDate: string, endDate: string) => {
  return apiClient.get<
    Array<{
      date: string;
      channel: string;
      totalBillingAmount: number;
      totalRefundBillingAmount: number;
      totalEstimatedSettlementAmount: number;
      pgFee: number;
      netProfit: number;
    }>
  >('/admin/v1/statistics/daily-net-sales-report', {
    params: { startDate, endDate },
  });
};
